import './recognition.css'

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { SpeechRecognition, SpeechRecognitionEvent } from '@/@types/global'
import { useAppContext } from '@/context/app'

export const SpeechRecognitionPopupBody = ({
  initialText = '',
  onApprove,
  onCancel,
  upperCase = false,
  removeSpaces = false,
}: {
  initialText?: string
  onApprove: (text: string) => void
  onCancel?: () => void
  upperCase?: boolean
  removeSpaces?: boolean
}) => {
  const { services, session, translates } = useAppContext()
  const [text, setText] = useState(initialText)
  const [isListening, setIsListening] = useState(false)

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const isListeningRef = useRef(false)
  const baseTextRef = useRef<string>(initialText)

  // NEW: keep latest options without re-creating recognition
  const upperCaseRef = useRef(upperCase)
  const removeSpacesRef = useRef(removeSpaces)
  const translatesRef = useRef(translates)

  useEffect(() => {
    upperCaseRef.current = upperCase
  }, [upperCase])

  useEffect(() => {
    removeSpacesRef.current = removeSpaces
  }, [removeSpaces])

  useEffect(() => {
    translatesRef.current = translates
  }, [translates])

  useLayoutEffect(() => {
    const unsafeWindow = window as any
    const SR = unsafeWindow.SpeechRecognition || unsafeWindow.webkitSpeechRecognition
    const lang = session?.user.lang || services.i18n.defaultLang() || 'pl'

    if (!SR) {
      const msg = translatesRef.current?.for(
        `errors.speech_recognition_not_supported`,
        'Speech recognition not supported in this browser',
      )
      alert(msg)
      return
    }

    const recognition: SpeechRecognition = new SR()
    recognitionRef.current = recognition

    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1
    recognition.lang = `${lang.toLowerCase()}-${lang.toUpperCase()}`

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transformTranscript = (s: string) => {
        let out = s ?? ''
        if (removeSpacesRef.current) out = out.replace(/\s+/g, '')
        if (upperCaseRef.current) out = out.toUpperCase()
        return out
      }

      let finalTranscript = ''
      let intTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const piece = transformTranscript(result[0].transcript)
        if (result.isFinal) finalTranscript += piece
        else intTranscript += piece
      }

      const removeSpacesNow = removeSpacesRef.current
      const finalClean = removeSpacesNow ? finalTranscript : finalTranscript.trim()
      const interimClean = removeSpacesNow ? intTranscript : intTranscript.trim()

      if (finalClean) {
        baseTextRef.current = removeSpacesNow
          ? `${baseTextRef.current}${finalClean}`
          : `${baseTextRef.current} ${finalClean}`.trim()
      }

      setText(
        removeSpacesNow
          ? `${baseTextRef.current}${interimClean}`
          : `${baseTextRef.current}${interimClean ? ` ${interimClean}` : ''}`.trim(),
      )
    }

    recognition.onerror = (event: any) => {
      const err = event?.error as string | undefined

      if (err === 'no-speech') return
      if (err === 'aborted') return

      if (err === 'not-allowed' || err === 'service-not-allowed') {
        const msg = translatesRef.current?.for(
          `errors.microphone_access`,
          'Microphone access was denied or is not allowed',
        )
        alert(msg)
        isListeningRef.current = false
        setIsListening(false)
        recognition.stop()
        return
      }

      if (err === 'audio-capture') {
        const msg = translatesRef.current?.for(
          `errors.microphone_not_found`,
          'Cannot access microphone (audio capture failed)',
        )
        alert(msg)
        isListeningRef.current = false
        setIsListening(false)
        recognition.stop()
        return
      }

      console.error('Error occurred in recognition:', err, event)
    }

    ;(recognition as any).onend = () => {
      if (!isListeningRef.current) return
      try {
        recognition.start()
      } catch {}
    }

    try {
      baseTextRef.current = initialText || ''
      recognition.start()
      isListeningRef.current = true
      setIsListening(true)
    } catch (e) {
      isListeningRef.current = false
      setIsListening(false)
      console.warn('Speech recognition could not auto-start:', e)
    }

    return () => {
      isListeningRef.current = false
      setIsListening(false)
      try {
        recognition.stop()
      } catch {}
      recognitionRef.current = null
    }
  }, [session?.user.lang, initialText])

  const stop = () => {
    isListeningRef.current = false
    setIsListening(false)
    try {
      recognitionRef.current?.stop()
    } catch {}
  }

  const start = () => {
    if (isListeningRef.current) return // NEW: guard against double-start
    try {
      recognitionRef.current?.start()
      isListeningRef.current = true
      setIsListening(true)
    } catch (e) {
      console.warn('Could not start recognition:', e)
    }
  }
  return (
    <div className="speech-popup-body">
      <div className="description-box">
        <textarea
          value={text}
          onChange={(e) => {
            const v = e.target.value
            baseTextRef.current = v
            setText(v)
          }}
          placeholder={translates?.for(`popup.speech_popup.placeholder`, '')}
          readOnly={isListening}
        />
      </div>

      <div
        className={isListening ? 'record-btn pulse' : 'record-btn'}
        onClick={() => (isListening ? stop() : start())}
      >
        {isListening
          ? translates?.for(`popup.speech_popup.stop_recording`)
          : translates?.for(`popup.speech_popup.record`)}
        <img
          src={`/public/img/icons/microphone.svg`}
          style={{ opacity: isListening ? 0.5 : 1 }}
        />
      </div>

      <div className="approve-btn" onClick={() => (stop(), onApprove(text.trim()))}>
        {translates?.for(`popup.speech_popup.approve`)}
      </div>

      {onCancel && (
        <div className="approve-btn" onClick={() => (stop(), onCancel())}>
          {translates?.for(`popup.speech_popup.cancel`)}
        </div>
      )}
    </div>
  )
}